import _ from 'lodash'
import qs from 'querystring'
import request from '@/utils/request'
import { API_PREFIX } from '@/utils/constants'

/**
 * @api /api/v1.Position/positionList
 * @method GET
 * @description 获取项目中所有的职位
 */
export async function find() {
  try {
    const res = await request.get(`${API_PREFIX}/api/v1.Position/positionList`)
    return res.data
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Position/positionInfo
 * @method GET
 * @description 获取职位下的信息
 *
 * @param {number} id 职位 ID
 *
 */
export async function get(params) {
  try {
    const query = {
      id: params.id,
      total: 99999, // 不需要分页，故传一个很大的每页记录数
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Position/positionInfo?${qstr}`)
    const data = res.data

    const output = {
      id: data.positionInfo.id,
      name: data.positionInfo.name,
      note: data.positionInfo.remark,
      staffCount: data.staffCount,
      staffs: data.staffList.data.map((item, index) => ({
        index: index + 1,
        id: item.id,
        username: item.userName,
        name: item.name,
        mobilePhone: item.mobile,
        departments: item.position,
      })),
    }
    output.shownStaffs = _.clone(output.staffs)
    return output
  } catch (e) {
    return null
  }
}

/**
 * @api /api/v1.Position/delPositionStaffs
 * @method POST
 * @description 删除职位下的职员
 *
 * @param {number} id 职位 ID
 * @param {number[]} userid 用户 ID
 *
 */
export async function removeStaffs(params) {
  try {
    const query = {
      id: params.roleId,
      userid: params.userIds,
    }
    await request.post(`${API_PREFIX}/api/v1.Position/delPositionStaffs`, query)
    return true
  } catch (e) {
    return false
  }
}


/**
 * @api /api/v1.Position/staffNotInThisPosition
 * @method GET
 * @description 获取不在某部门下的用户
 *
 * @param {number} id 职位 ID
 * @param {number} page 页码
 * @param {number} total 每页记录数
 * @param {string} search 职员搜索关键字
 *
 */
export async function findExternalStaffs(params) {
  try {
    const query = {
      id: params.id,
      page: params.page,
      total: params.pageSize,
    }
    if (params.keyword) {
      query.search = params.keyword
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Position/staffNotInThisPosition?${qstr}`)
    const paginate = res.data.page
    const list = res.data.data
    return {
      page: paginate.current_page,
      pageSize: paginate.per_page,
      total: paginate.total,
      totalPages: paginate.last_page,
      results: list.map((item, index) => ({
        index: (paginate.current_page - 1) * paginate.per_page + index + 1,
        id: item.id,
        username: item.userName,
        name: item.name,
        mobilePhone: item.mobile,
      })),
    }
  } catch (e) {
    return {
      page: params.page,
      pageSize: params.pageSize,
      total: 0,
      totalPages: 1,
      results: [],
    }
  }
}

/**
 * @api /api/v1.Position/linkPositionAndStaff
 * @method POST
 * @description 添加用户到职位
 *
 * @param {number} id 职位 ID
 * @param {number[]} userid 用户 ID
 *
 */
export async function addStaffs(params) {
  try {
    const query = {
      id: params.roleId,
      userid: params.userIds,
    }
    await request.post(`${API_PREFIX}/api/v1.Position/linkPositionAndStaff`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Position/editPosition
 * @method GET
 * @description 编辑职位
 *
 * @param {number} id 职位 ID
 * @param {string} name 职位名称
 * @param {string} remark 备注
 *
 */
export async function update(params) {
  try {
    const query = {
      id: params.id,
      name: params.name,
      remark: params.note,
    }
    const qstr = qs.stringify(query)
    await request.get(`${API_PREFIX}/api/v1.Position/editPosition?${qstr}`)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Position/delPosition
 * @method GET
 * @description 删除职位
 *
 * @param {number} id 职位 ID
 *
 */
export async function remove(params) {
  try {
    const query = {
      id: params.id,
    }
    const qstr = qs.stringify(query)
    await request.get(`${API_PREFIX}/api/v1.Position/delPosition?${qstr}`)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Auth/getProjectAuthList
 * @method GET
 * @description 获取项目的所有权限的列表
 *
 * @param {number} sys 权限所属系统，1: 智慧现场; 2: 智慧工厂; 3: 智慧物流; 4: 智慧运营; 5: 物联网
 *
 */
export async function findAllPermissionGroups() {
  try {
    const query = {
      sys: parseInt(sessionStorage.getItem('sys'), 10) || 1, // 当前固定取 “智慧现场” 的权限
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Auth/getProjectAuthList?${qstr}`)
    return res.data.map(item => ({
      name: item.auth_cate_name,
      children: item.children.map(o => [o]), // 为了解决 el-tree 渲染的问题, 需要最外层是数组
    }))
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Position/addPosition
 * @method POST
 * @description 添加职位
 *
 * @param {string} name 职位名称
 * @param {string} remark 备注
 * @param {Object[]} auth 权限
 * @param {number} auth.auth_id 权限 ID（包括父级和子级权限）
 * @param {number} auth.sys 权限所属系统，1: 智慧现场; 2: 智慧工厂; 3: 智慧物流; 4: 智慧运营; 5: 物联网
 *
 */
export async function create(params) {
  try {
    const query = {
      name: params.name,
      remark: params.note,
      auth: params.permissions.map(id => ({
        auth_id: id,
        sys: parseInt(sessionStorage.getItem('sys'), 10) || 1, // 当前固定取 “智慧现场” 的权限
      })),
    }
    const res = await request.post(`${API_PREFIX}/api/v1.Position/addPosition`, query)
    return parseInt(res.data, 10)
  } catch (e) {
    return -1
  }
}

/**
 * @api /api/v1.Auth/getPositionAuthList
 * @method GET
 * @description 获取职位已设置的权限
 *
 * @param {number} id 职位 ID
 * @param {number} sys 权限所属系统，1: 智慧现场; 2: 智慧工厂; 3: 智慧物流; 4: 智慧运营; 5: 物联网
 *
 */
export async function findRolePermissions(params) {
  try {
    const query = {
      id: params.id,
      sys: parseInt(sessionStorage.getItem('sys'), 10) || 1, // 当前固定取 “智慧现场” 的权限
    }
    const qstr = qs.stringify(query)
    const res = await request.get(`${API_PREFIX}/api/v1.Auth/getPositionAuthList?${qstr}`)
    return res.data.all_auth
  } catch (e) {
    return []
  }
}

/**
 * @api /api/v1.Position/setPositionAuth
 * @method POST
 * @description 设置职位的权限
 *
 * @param {number} id 职位 ID
 * @param {number[]} auth 权限 ID 数组
 * @param {number} set_type 修改类型，1: 添加权限; 2: 删除权限
 * @param {number} sys 权限所属系统，1: 智慧现场; 2: 智慧工厂; 3: 智慧物流; 4: 智慧运营; 5: 物联网
 *
 */
export async function changePermissions(params) {
  try {
    const query = {
      id: params.id,
      auth: params.permissions,
      set_type: params.add ? 1 : 2,
      sys: parseInt(sessionStorage.getItem('sys'), 10) || 1, // 当前固定取 “智慧现场” 的权限
    }
    await request.post(`${API_PREFIX}/api/v1.Position/setPositionAuth`, query)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @api /api/v1.Position/sortPosition
 * @method GET
 * @description 角色排序
 * @param {number} id 被拖动角色id
 * @param {number} target_id 目标角色id
 * @param {number} move_type 1目标角色的上面2目标角色的下面
 */
export async function sortPosition(params) {
  try {
    console.log(API_PREFIX)
    const qstr = qs.stringify(params)
    const res = await request.get(`${API_PREFIX}/api/v1.Position/sortPosition?${qstr}`)
    return res.data
  } catch (e) {
    return []
  }
}
